import { LayoutEngine } from '@src/editor/models/layout/LayoutEngine';
import { useSpeakerLabels } from '@src/hooks/useSpeakerLabels';
import { getFirstDetectionForFace } from '@src/services/framing/faceFrequency';
import { mapFaceToSpeaker } from '@src/services/framing/mapFaceToSpeaker';
import { planSceneReframe } from '@src/services/framing/planSceneReframe';
import {
  listSceneFaces,
  speakerForFace,
} from '@src/services/framing/sceneFaceAssignment';
import { SmartFramingService } from '@src/services/framing/SmartFramingService';
import { SceneService } from '@src/services/SceneService';
import { useStore } from '@src/stores-v2/StoreContext';
import { useVideoCreatorStore } from '@src/stores-v2/VideoCreatorStoreContext';
import { extractErrorInfo } from '@src/utility/error';
import { LogManager } from '@src/utility/Logger';
import parseAspectRatio from '@src/utility/parseAspectRatio';
import { fetchSpeakerVoiceMappings } from '@src/utility/speakerUtils';
import { resolveStoryPlaybackSource } from '@src/utility/storyPlaybackSource';
import { useCallback, useEffect, useMemo, useState } from 'react';

import type { IVideoEditor } from '@src/editor/interface/IVideoEditor';
import type { RendererSyncService } from '@src/editor/services/RendererSyncService';
import type { AutosaveStatus } from '@src/hooks/useAutosave';

const logger = LogManager.getLogger('feature:useReframeSpeakersTab');

export interface ReframeSpeakersTabFace {
  faceLabel: string;
  /** A detection to cut the thumbnail from, in source-media time. */
  thumbnail: { time: number; box: [number, number, number, number] } | null;
  /** Transcript speaker framing treats this face as, or null. */
  speakerLabel: string | null;
}

export interface ReframeSpeakersTabSpeaker {
  speakerLabel: string;
  name: string;
  /** Linked to a Dato speaker, so it can hold a face. */
  isNamed: boolean;
}

export interface ReframeSpeakersTabView {
  faces: ReframeSpeakersTabFace[];
  speakers: ReframeSpeakersTabSpeaker[];
  videoUrl: string | undefined;
  /** The story's face data hasn't arrived yet. */
  isLoading: boolean;
  /** Idle until the first change this session; the modal's Done reads it. */
  saveStatus: AutosaveStatus;
  assign(faceLabel: string, speakerLabel: string | null): Promise<boolean>;
  /** Assigns the face once its transcript speaker was just linked to `datoSpeakerId`. */
  assignAfterLinking(
    faceLabel: string,
    speakerLabel: string,
    datoSpeakerId: string,
  ): Promise<boolean>;
}

/** Smart framing defaults to 9:16; every other aspect needs its ratio. */
export function resolveTargetAspectRatio(
  aspectRatio: string,
): number | undefined {
  if (aspectRatio === '9:16') return undefined;
  return parseAspectRatio(aspectRatio) ?? undefined;
}

/**
 * The reframe modal's Speakers tab (PLAT-11992): the faces in the scene being
 * framed and who each one is. Assigning a face saves it to the story — the
 * same override Face Mapping writes — and reframes the scenes it affects in
 * one undo.
 *
 * Reads the face data already loaded with the story, and saves without
 * re-fetching it: the picker shows the choice straight away instead of
 * reloading the list after every change.
 */
export function useReframeSpeakersTab({
  active,
  editor,
  aspectRatio,
  sceneId,
  rendererSyncService,
  onAfterApply,
}: {
  active: boolean;
  editor: IVideoEditor | undefined;
  aspectRatio: string;
  sceneId: string | undefined;
  rendererSyncService: RendererSyncService | null;
  onAfterApply?: () => void;
}): ReframeSpeakersTabView {
  const { speakerFaceMapStore } = useStore();
  const story = useVideoCreatorStore().story;
  const storyId = story?.id;
  const { getSpeakerName } = useSpeakerLabels();
  const [saveStatus, setSaveStatus] = useState<AutosaveStatus>('idle');
  /** The speaker picked for a face this session, shown before the save lands. */
  const [pickedSpeakers, setPickedSpeakers] = useState<
    Record<string, string | null>
  >({});

  // Already cached by the transcript's speaker labels; only says whether a
  // transcript speaker has a Dato speaker yet.
  const [voiceMappings, setVoiceMappings] = useState<Record<string, string>>(
    {},
  );
  useEffect(() => {
    if (!active || !storyId) return;
    let cancelled = false;
    fetchSpeakerVoiceMappings(storyId)
      .then(result => {
        if (!cancelled) setVoiceMappings(result?.mappings ?? {});
      })
      .catch(error =>
        logger.warn('Failed to read speaker mappings', extractErrorInfo(error)),
      );
    return () => {
      cancelled = true;
    };
  }, [active, storyId]);

  const framer = useMemo(
    () =>
      editor
        ? new SmartFramingService(
            editor,
            new SceneService(),
            new LayoutEngine(),
          )
        : null,
    [editor],
  );
  const faceDetections = speakerFaceMapStore.faceDetections;
  const transcript = editor?.getOriginalTranscript();
  // Real, corrected or guessed — whatever framing runs on today.
  const speakerMap = speakerFaceMapStore.mapForFraming(transcript);

  const sceneFaceLabels = useMemo(() => {
    if (!active || !framer || !sceneId || !faceDetections?.length) return [];
    const sceneInfo = framer
      .getSceneInfos()
      .find(({ scene }) => scene.id === sceneId);
    if (!sceneInfo) return [];
    return listSceneFaces(
      framer.getSceneFaceDetections(sceneInfo, [...faceDetections]),
    ).map(({ faceLabel }) => faceLabel);
  }, [active, framer, sceneId, faceDetections]);

  const faces = sceneFaceLabels.map(faceLabel => {
    const first = faceDetections
      ? getFirstDetectionForFace([...faceDetections], faceLabel)
      : null;
    return {
      faceLabel,
      thumbnail: first ? { time: first.time, box: first.box } : null,
      speakerLabel:
        faceLabel in pickedSpeakers
          ? pickedSpeakers[faceLabel]
          : speakerForFace(speakerMap, faceLabel),
    };
  });

  const speakers = useMemo(() => {
    const labels = new Set<string>();
    for (const word of transcript ?? []) {
      if (word.type === 'text' && word.speaker_label) {
        labels.add(word.speaker_label);
      }
    }
    return [...labels].sort().map(speakerLabel => ({
      speakerLabel,
      name: getSpeakerName(speakerLabel),
      isNamed: !!voiceMappings[speakerLabel],
    }));
  }, [transcript, getSpeakerName, voiceMappings]);

  const save = useCallback(
    async (
      faceLabel: string,
      speakerLabel: string | null,
      datoSpeakerId?: string,
    ): Promise<boolean> => {
      if (!storyId || !framer || !faceDetections) return false;
      const previousSpeaker = speakerForFace(speakerMap, faceLabel);
      setPickedSpeakers(previous => ({
        ...previous,
        [faceLabel]: speakerLabel,
      }));
      setSaveStatus('saving');
      const mapped = await mapFaceToSpeaker({
        storyId,
        faceLabel,
        speakerLabel,
        datoSpeakerId,
      });
      if (!mapped.ok) {
        logger.warn('Could not save the face mapping', {
          faceLabel,
          speakerLabel,
          error: mapped.error,
        });
        setPickedSpeakers(previous => {
          const next = { ...previous };
          delete next[faceLabel];
          return next;
        });
        setSaveStatus('error');
        return false;
      }
      speakerFaceMapStore.set(storyId, mapped.value);
      setSaveStatus('saved');

      const sceneUpdates = planSceneReframe({
        framer,
        faceDetections: [...faceDetections],
        speakerMap: speakerFaceMapStore.mapForFraming(transcript) ?? {},
        speakerLabels: new Set(
          [speakerLabel, previousSpeaker].filter(
            (label): label is string => !!label,
          ),
        ),
        targetAspectRatio: resolveTargetAspectRatio(aspectRatio),
      });
      const updated = rendererSyncService?.commitSceneUpdates(sceneUpdates);
      logger.info('Assigned a face and reframed its scenes', {
        faceLabel,
        speakerLabel,
        previousSpeaker,
        reframedScenes: updated?.size ?? 0,
      });
      onAfterApply?.();
      return true;
    },
    [
      storyId,
      framer,
      faceDetections,
      speakerMap,
      speakerFaceMapStore,
      transcript,
      aspectRatio,
      rendererSyncService,
      onAfterApply,
    ],
  );

  const assign = useCallback(
    (faceLabel: string, speakerLabel: string | null) =>
      save(faceLabel, speakerLabel),
    [save],
  );

  const assignAfterLinking = useCallback(
    (faceLabel: string, speakerLabel: string, datoSpeakerId: string) => {
      setVoiceMappings(previous => ({
        ...previous,
        [speakerLabel]: datoSpeakerId,
      }));
      return save(faceLabel, speakerLabel, datoSpeakerId);
    },
    [save],
  );

  return {
    faces,
    speakers,
    videoUrl:
      resolveStoryPlaybackSource(story, 'lowest-first').mp4Url ?? undefined,
    isLoading: !faceDetections,
    saveStatus,
    assign,
    assignAfterLinking,
  };
}
